import {
    Card,
    Grid,
    Typography,
    Button,
    Divider,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TablePagination,
    TableRow,
    Box,
    TableCell,
    Tooltip
  } from '@mui/material';
  import React, { ChangeEvent, Fragment, useCallback, useEffect, useRef, useState } from 'react';
  import FilterListIcon from '@mui/icons-material/FilterList';
import { useForm } from 'react-hook-form';
import { WorkModelFilter } from 'src/models/leave';
import SingleDatePicker from './common/SingleDatePicker';
import { compareObjects } from 'src/utils/validate';
import { TableHeaderCell } from 'src/components/TableHeaderCell';
import NoRecord from '../resourcePool/NoRecord';
import { withStyles } from '@mui/styles';
import moment from 'moment';
import WorkModelTypeMultiSelect from './common/WorkModelTypeMultiSelect';
import EmployeeMultiSelect from './common/EmployeeMultiSelect';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { AppActions } from 'src/store/AppReducer';
import { deleteWorkModelById, getWorkModelConfigurations } from 'src/services/leaveService';
import { TableColumnCell } from 'src/components/TableColumnCell';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ToggleModal from './ToggleModal';
import SnackBar from 'src/components/SnackBar/SnackBar';
import ConfirmationModal from '../common/confirmationModal/ConfirmationModal';
import { useAppSelector } from 'src/hooks/hooks';
  
interface WorkModelProps {
  permissions: any;
}
  
const WorkModelView: React.FC<WorkModelProps> = ({ permissions }) => {

  const { isLoading } = useAppSelector((state) => state.appReducer);
  const [resources, setResources] = useState<any>([]);
  const [datesAndDayNames, setDatesAndDayNames] = useState<any>([]);
  
  const [toggleFilter, setToggleFilter] = useState(false);
  const [isFilterChanged, setIsFilterChanged] = useState(true);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isClearDisabled, setIsClearDisabled] = useState(false);
  const [total, setTotal] = useState<number>(0);

  const pagination_wrapper_ref = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('');
  const [snackBarMessage, setSnackBarMessage] = useState('');
  const [sortName, setSortName] = useState('');

  const [viewType, setViewType] = useState('ADD');
  const [openModal, setOpenModal] = useState(false);
  
  const [openConfirmationDelete, setOpenConfirmationDelete] = useState(false);
  const [workModelDeleteID, setWorkModelDeleteID] = useState('');

  const [workModelData, setWorkModelData] = useState<any>([]);

  let initialFilterValues = {
    employees: null,
    workModel: null,
    startDate: null,
    endDate: null,
    limit: 10,
    offset: 0,
    isSubmitted: true
  } as WorkModelFilter;
  const [workModelFilter, setWorkModelFilter] = useState(initialFilterValues as WorkModelFilter);
  const [prevWorkModelFilter, setPrevWorkModelFilter] = useState(initialFilterValues as WorkModelFilter);

  const { register, handleSubmit, reset, setValue, watch } =
  useForm<WorkModelFilter>({
    defaultValues: initialFilterValues
  });

  const startDate = watch('startDate');

  // Function to format the date to "DD-MMM"
  const formatDate = (date) => {
    const options = { day: '2-digit', month: 'short' };
    return date.toLocaleDateString('en-GB', options).replace(/ /g, '-').toLowerCase();
  }

  // Function to get the day name from a date
  const getDayName = (date) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[date.getDay()];
  }

  useEffect(() => {
    if (resources?.length > 0 && sortName === 'asc') {
      setResources(resources.sort((a, b) => a.employeeName.localeCompare(b.employeeName)));
    } else if (resources?.length > 0 && sortName === 'desc') {
      setResources(resources.sort((a, b) => b.employeeName.localeCompare(a.employeeName)));
    }
  }, [sortName]);

  // Function to get all dates and their day names between start date and end date
  const getDatesAndDayNames = (startDate, endDate) => {
    const datesArray = [];

    let currentDate = new Date(startDate);

    while (currentDate <= endDate) {
        datesArray.push(
          {
            date: new Date(currentDate),
            day: formatDate(new Date(currentDate)),
            dayName: getDayName(currentDate)
          }
        );  // Clone the current date
        
        // Increment the date by one day
        currentDate.setDate(currentDate.getDate() + 1);
    }

    setDatesAndDayNames(datesArray)
  }

  const onSubmit = () => {
    const filter = {
      ...workModelFilter,
      isSubmitted: true,
      offset: 0,
      limit: 10
    }

    if(isFilterChanged){
      getDatesAndDayNames(
        workModelFilter.startDate ? workModelFilter.startDate : new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        workModelFilter.endDate ? workModelFilter.endDate : new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
      )
      setWorkModelFilter(filter);
      setPrevWorkModelFilter(filter);
      setIsFilterChanged(false);
    } 
  };

  //clear the filter
  const resetFilterResult = () => {
    reset();

    const result = compareObjects(initialFilterValues, workModelFilter, "isSubmitted");
    !result && setWorkModelFilter({
      ...initialFilterValues,
      startDate: null,
      endDate: null,
    });

    setPrevWorkModelFilter({
      ...initialFilterValues,
      startDate: null,
      endDate: null,
    });
  };

  const isEndDateValid = (endDate, startDate) => {
    return new Date(endDate) >= new Date(startDate) || (endDate == null && startDate == null);
  };

  useEffect(() => {
    if(workModelFilter.isSubmitted){
      getDatesAndDayNames(
        workModelFilter.startDate ? workModelFilter.startDate : new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        workModelFilter.endDate ? workModelFilter.endDate : new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
      )
      dispatch(AppActions.showLoader());
      const payload = {
        employeeIds: workModelFilter.employees,
        types: workModelFilter.workModel,
        fromDate: workModelFilter.startDate ?  moment(workModelFilter.startDate).format('YYYY-MM-DD') : null,
        toDate: workModelFilter.endDate ? moment(workModelFilter.endDate).format('YYYY-MM-DD') : null
      };

      const fetchAllWorkModelConfigurations = (payload) => {
        getWorkModelConfigurations(payload,
          workModelFilter.offset,
          workModelFilter.limit
        ).then((res) => {
          const data = res?.data?.data
          setResources(data?.configurationSummery);
          setTotal(data?.recordCount);
          dispatch(AppActions.hideLoader());
        });
      };
  
      fetchAllWorkModelConfigurations(payload);
      /* setResources(res); */
      setWorkModelFilter((prev) => ({
        ...prev,
        isSubmitted: false
      }))
    }
  }, [workModelFilter]);


  useEffect(() => {
    const filter = {
      startDate: workModelFilter.startDate,
      endDate: workModelFilter.endDate,
      employees: workModelFilter.employees,
      workModel: workModelFilter.workModel
    };

    const isNullish = Object.values(filter).every((value) => {
      if (value === null) {
        return true;
      }

      return false;
    });

    if (Object.keys(filter).length === 0 || isNullish) {
      setIsDisabled(true);
      setIsClearDisabled(true);
    } else {
      if(workModelFilter.startDate && workModelFilter.endDate == null){
        setIsDisabled(true);
      } else {
        setIsDisabled(false);
      }
      setIsClearDisabled(false);
    }
  }, [workModelFilter]);

  // custom table row with different color
  const CustomTableRow = withStyles({
    root: {
      background: '#E6E9FF !important',
      height: '3rem'
    }
  })(TableRow);


  //handele pagination page change
  const handlePageChange = (event: any, newPage: number): void => {
    const validate = isEndDateValid(prevWorkModelFilter.endDate, prevWorkModelFilter.startDate);
    setWorkModelFilter({
      ...prevWorkModelFilter,
      offset: newPage,
      limit: workModelFilter.limit,
      isSubmitted: true,
      startDate: validate ? prevWorkModelFilter.startDate : null
    });
  };

  //handele pagination limit change
  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const validate = isEndDateValid(prevWorkModelFilter.endDate, prevWorkModelFilter.startDate);
    setWorkModelFilter({
      ...prevWorkModelFilter,
      limit: parseInt(event.target.value),
      isSubmitted: true,
      startDate: validate ? prevWorkModelFilter.startDate : null
    });
  };


  // custom table cloumn cell
  const CustomTableCloumnCell = ({content, sticky}) => {
    return (
      <TableCell
        style={{
          background: 'white',
          whiteSpace: 'nowrap',
          alignItems: 'center',
          color: '#232C68',
          fontSize: '11px',
          position: sticky ? 'sticky' : 'relative',
          left: sticky ? 0 : '',
          zIndex: sticky ? 10 : 1,
          borderRight: '1px solid rgba(34, 51, 84, 0.1)',
          borderBottom: '1px solid rgba(34, 51, 84, 0.1)'
        }}>
           <Typography
            paddingBottom="0.25rem"
            paddingTop="0.5rem"
            variant="body2"
            style={{ fontSize: '11px' }}
            gutterBottom
            noWrap
          >
            {content}
          </Typography>
      </TableCell>
    )
  }

  const handleToggle = useCallback(() => {
    setOpenModal(prevOpenModal => !prevOpenModal);
  }, []);

  const getType = (type: string) => {
    if(type === 'IN_OFFICE'){
      return 'In Office';
    } else if(type === 'HYBRID'){
      return 'Hybrid';
    } else{
      return 'Permanent WFH';
    }
  }

  const toggleConfirmationDelete = () => {
    setOpenConfirmationDelete(!openConfirmationDelete);
  };

  const deleteWorkModel = () => {
    deleteWorkModelById(workModelDeleteID)
      .then(() => {
        dispatch(
          AppActions.toggleSnackBarOpen({
            message: 'You have deleted the work model successfully.',
            severity: 'success'
          })
        );
        setWorkModelFilter((prev) => ({
          ...prev,
          isSubmitted: true
        }))
      })
      .catch((error) => {
        console.log(error);
        dispatch(
          AppActions.toggleSnackBarOpen({
            message:
              error?.request?.response ? error?.request?.response : 'Unable to delete a work model',
            severity: 'error'
          })
        );
      });

    toggleConfirmationDelete();
  };

  return (
    <Fragment>
      <Grid container sx={{ mt: 2, mb: 2 }} justifyContent="space-between">
        <Grid item>
          <Typography
            style={{
              marginLeft: '0px',
              fontWeight: '700',
              fontSize: '16px',
              fontFamily:
                'Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"'
            }}
          >
            Work Model
          </Typography>
        </Grid>
        <Grid item>
        {permissions.some(perm =>
            perm.name === 'Add Work Model' && perm.value === true
          ) && 
          <Button
            sx={{
              mt: { xs: 2, md: 0 },
              fontSize: '11px',
              fontWeight: 'normal'
            }}
            startIcon={<AddTwoToneIcon fontSize="small" />}
            variant="contained"
            style={{ height: '1.75rem' }}
            onClick={() => {
              setWorkModelData({
                employeeIds: null,
                fromDate: null,
                toDate: null,
                type: null,
                frequency: null,
                selectedDates: []
              });
              setViewType('ADD');
              setOpenModal(prevOpenModal => !prevOpenModal);
            }}
          >
            Add Work Model
          </Button>
        }
        </Grid>
      </Grid>
      <div className='resource-pool'>
        <Card>
          <Grid sx={{ px: 2, py: 2 }} item xs={6}>
            <Button
              id="btn-setToggleFilter"
              sx={{
                mt: { xs: 2, md: 0 },
                color: `${!toggleFilter && 'gray'}`
              }}
              variant="outlined"
              startIcon={<FilterListIcon style={{ fontSize: '15px' }} />}
              onClick={() => {
                setToggleFilter(!toggleFilter);
              }}
              style={{ height: '1.75rem', fontSize: '12px' }}
            >
              Filters
            </Button>
          </Grid>
          {toggleFilter && (
            <Grid px={2} pb={2} style={{margin: 0}}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid
                  container
                  spacing={1}
                  style={{
                    justifyContent: 'between',
                    flexWrap: 'nowrap',
                    alignItems: 'center'
                  }}
                >
                  <Grid item xs={3} width='20%'>
                    <EmployeeMultiSelect
                      {...register('employees')}
                      value={workModelFilter?.employees || []}
                      onChange={(e: any) => {
                        setWorkModelFilter((prev) => ({
                          ...prev,
                          employees: e.target.value
                        }));
                        setValue('employees', e.target.value);
                        setIsFilterChanged(true);
                      }}/>
                  </Grid>
                  <Grid item xs={3} width='20%'>
                    <WorkModelTypeMultiSelect
                      {...register('workModel')}
                      value={workModelFilter?.workModel || []}
                      onChange={(e: any) => {
                        setWorkModelFilter((prev) => ({
                          ...prev,
                          workModel: e.target.value
                        }));
                        setValue('workModel', e.target.value);
                        setIsFilterChanged(true);
                      }}/>
                  </Grid>
                  <Grid item xs={3} width='20%'>
                    <SingleDatePicker
                      {...register('startDate')}
                      fullWidth={true}
                      label='Start Date'
                      value={workModelFilter?.startDate || null}
                      onChange={(e: any) => {
                        setWorkModelFilter((prev) => ({
                          ...prev,
                          startDate: e,
                          endDate: e > prev.endDate ? null : prev.endDate
                        }));
                        setValue('startDate', e);
                        setIsFilterChanged(true);
                      }}/>
                  </Grid>
                  <Grid item xs={3} width='20%'>
                    <SingleDatePicker
                      fullWidth={true}
                      {...register('endDate', {
                        validate: value => isEndDateValid(value, startDate) 
                      })}
                      disabled={workModelFilter?.startDate === null}
                      minDate={workModelFilter?.startDate}
                      label='End Date'
                      value={workModelFilter?.endDate || null}
                      onChange={(e: any) => {
                        setWorkModelFilter((prev) => ({
                          ...prev,
                          endDate: e
                        }));
                        setValue('endDate', e);
                        setIsFilterChanged(true);
                      }}/>
                      <div style={{fontSize: '9px', color: 'red', position: 'absolute', marginLeft: '15px'}}>
                        {workModelFilter?.startDate && workModelFilter?.endDate == null &&
                        "End date is mandatory field"}
                      </div>
                  </Grid>
                </Grid>
                <Grid
                  container
                  spacing={1}
                  style={{
                    justifyContent: 'between',
                    flexWrap: 'nowrap',
                    alignItems: 'center',
                    marginTop: '5px',
                  }}
                >
                  <Grid
                    item xs={3}
                    width='60%'
                    style={{
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      marginLeft: 'auto',
                      marginTop: '8px',
                      display: 'flex'
                    }}
                  >
                    <Button
                      sx={{ mt: { xs: 2, md: 0 }, mr: 1 }}
                      variant="outlined"
                      onClick={resetFilterResult}
                      disabled={isClearDisabled}
                      className={isClearDisabled &&'clear-btn'}
                      style={{ height: '1.75rem', fontSize: '12px' }}
                      id="btn-resetFilterResult"
                    >
                      Clear
                    </Button>
                    <Button
                      sx={{ mt: { xs: 2, md: 0 } }}
                      id="btn-onSubmit"
                      variant="outlined"
                      type="submit"
                      disabled={isDisabled}
                      className={isDisabled &&'filter-btn'}
                      style={{ height: '1.75rem', fontSize: '12px' }}
                    >
                      Filter
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Grid>
          )}
          <Divider />
          <TableContainer className="tableContainer" style={{ height: (resources?.length === 0 || !resources) ? '250px' : 'auto' }}>
            <Table style={{ borderCollapse: 'separate', position: 'relative' }} size="small">
              <TableHead>
                <CustomTableRow style={{position: 'sticky', top: 0, zIndex: 1}}>
                  <TableHeaderCell 
                    content="Employee Name" 
                    smallFont 
                    sort
                    sticky 
                    leftSticky
                    setSortType={setSortName}
                    sortType={sortName}/>
                  <TableHeaderCell content="Work Model" smallFont/>
                  {datesAndDayNames?.length > 0 && datesAndDayNames?.map((d, i) => {
                    return (
                      <TableCell
                        key={i}
                        style={{
                          width: '180px',
                          whiteSpace: 'nowrap',
                          alignItems: 'center',
                          color: '#232C68',
                          fontWeight: '700',
                          fontSize: '11px',
                          background: '#E6E9FF',
                          borderRight: `${d.dayName === 'Sunday' ? '1px solid black' : 'auto'}`,
                          borderLeft: `${d.dayName === 'Monday' ? '1px solid black' : '1px solid rgba(34, 51, 84, 0.1)'}`
                        }}>
                        <Box display='flex' alignItems='center' flexDirection='column'>
                          <Typography
                            mb={0}
                            variant="body2"
                            style={{ fontSize: '11px', fontWeight: '700', }}
                            gutterBottom
                            noWrap
                          >
                            {d.dayName}
                          </Typography>
                          <Typography
                            mb={0}
                            variant="body2"
                            style={{ fontSize: '11px', fontWeight: '700', }}
                            gutterBottom
                            noWrap
                          >
                            {d.day}-{d.date.getFullYear()}
                          </Typography>
                        </Box>
                      </TableCell>
                    )
                  })}
                  <TableHeaderCell content="Action" smallFont sticky/>
                </CustomTableRow>
              </TableHead>
              <TableBody>
                {resources &&
                  resources?.length > 0 &&
                  resources.map((emp, i) => {
                    return (
                      <TableRow
                        style={{borderBottom: '1px solid #eee'}}
                        hover
                        key={emp.id}
                      >
                        <CustomTableCloumnCell content={emp?.employeeName} sticky/>
                        <CustomTableCloumnCell content={getType(emp?.type)} sticky={false}/>
                        {datesAndDayNames?.length > 0 && datesAndDayNames?.map((d, i) => {
                          const isDateExist = emp?.type === 'PERMANENT_WFH' ? moment(d.date).isBetween(emp?.fromDate, emp?.toDate, null, '[]')
                            : emp?.selectedDates?.includes(moment(d.date).format('YYYY-MM-DD'));
                          return (
                            <TableCell
                              className={isDateExist && 'date-row'}
                              key={i}
                              style={{
                                background: isDateExist && 'rgba(98, 103, 253, 0.35)',
                                whiteSpace: 'nowrap',
                                alignItems: 'center',
                                fontWeight: '700',
                                fontSize: '11px',
                                borderBottom: `${isDateExist ? '1px solid #eee' : '1px solid rgba(34, 51, 84, 0.1)'}`,
                                borderLeft: `${d.dayName === 'Monday' ? '1px solid black' : '1px solid rgba(34, 51, 84, 0.1)'}`,
                                borderRight: `${d.dayName === 'Sunday' ? '1px solid black' : '1px solid #eee'}`,
                                
                              }}/>
                          )
                        })}
                        {permissions.some(perm =>
                          (perm.name === 'Edit Work Model' && perm.value === true) ||
                          (perm.name === 'View Work Model' && perm.value === true) || 
                          (perm.name === 'Delete Work Model' && perm.value === true)
                        ) &&
                          <TableColumnCell sticky>
                            <div style={{ background: 'white', marginLeft: 'auto', display: 'flex', flexDirection: 'row'}}>
                              {permissions.some(perm =>
                                (perm.name === 'Edit Work Model' && perm.value === true)
                              ) &&
                                <Tooltip title="Edit" placement="top" >
                                  <EditIcon
                                    id={"btn-edit-" + i}
                                    onClick={() => {
                                      setWorkModelData(emp);
                                      setViewType('UPDATE');
                                      handleToggle();
                                    }}
                                    style={{
                                      fontSize: '17px',
                                      cursor: 'pointer'
                                    }}
                                  />
                                </Tooltip>
                              }
                              {permissions.some(perm =>
                                (perm.name === 'View Work Model' && perm.value === true)
                              ) &&
                                <Tooltip title="View" placement="top" >
                                  <VisibilityIcon
                                    id={"btn-view" + i}
                                    onClick={() => {
                                      setWorkModelData(emp);
                                      setViewType('VIEW');
                                      handleToggle();
                                    }}
                                    style={{
                                      fontSize: '17px',
                                      cursor: 'pointer'
                                    }}
                                  />
                                </Tooltip>
                              }
                              {permissions.some(perm =>
                                (perm.name === 'Delete Work Model' && perm.value === true)
                              ) &&
                                <Tooltip title="Delete" placement="top" >
                                  <DeleteOutlineIcon
                                    id={'btn-delete-' + i}
                                    onClick={() => {
                                      setWorkModelDeleteID(emp.id);
                                      toggleConfirmationDelete();
                                    }}
                                    style={{
                                      fontSize: '17px',
                                      cursor: 'pointer'
                                    }}
                                  />
                                </Tooltip>
                              }
                            </div>
                          </TableColumnCell>
                        }
                      </TableRow>
                    );
                  })
                }
              </TableBody>
            </Table>
            {(resources?.length === 0 || !resources) && (
              <NoRecord/>
            )}
          </TableContainer>  
          <Box p={1} ref={pagination_wrapper_ref} sx={{borderTop: '1px solid #eee'}}>
            <TablePagination
              sx={{
                fontSize: '12px',
                '.MuiTablePagination-displayedRows': {
                  fontSize: '12px'
                },
                '.MuiTablePagination-selectLabel': {
                  fontSize: '12px'
                }
              }}
              component="div"
              count={total ? total : 0}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleLimitChange}
              page={workModelFilter.offset}
              rowsPerPage={workModelFilter.limit}
              rowsPerPageOptions={[10, 20, 30, 40, 50]}
            />
          </Box>
        </Card>
      </div>
      {openModal && 
        <ToggleModal
          open={openModal}
          toggle={handleToggle}
          viewType={viewType}
          setSubmmited={() => {
            setWorkModelFilter({
              ...initialFilterValues,
              isSubmitted: true,
            });
          }}
          workModelData={workModelData}
          setSnackBarOpen={setSnackBarOpen}
          setSnackbarSeverity={setSnackbarSeverity}
          setSnackBarMessage={setSnackBarMessage}
        />
      }
      <SnackBar
        snackBarOpen={snackBarOpen}
        setSnackBarOpen={setSnackBarOpen}
        snackBarMessage={snackBarMessage}
        snackbarSeverity={snackbarSeverity}
      />
      <ConfirmationModal
        isLoading={isLoading}
        open={openConfirmationDelete}
        toggle={toggleConfirmationDelete}
        message={`Are you sure want to delete this work model?`}
        onConfirm={() => deleteWorkModel()}
        noToggle={toggleConfirmationDelete}
      />
    </Fragment>
  );
};

export default WorkModelView;
  